import { Component, useMemo, type ReactNode } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import { ListRepresentation } from '../../views/ListRepresentation';
import { AppServicesContext, useAppServices } from '../../views/ViewContext';
import type { CustomEditor, EditorConfig, PreviewFormat, PropertyGroup } from '../editor/editor-types';
import { getAt, parsePointer, type Pointer } from '../editor/json-pointer';
import { describeProperties, type PropertyDescriptor, type SchemaNode, type SchemaRegistry } from '../editor/schema-properties';
import { fieldEditor, groupEditor, presetsEditor, PRESET_LABELS, quickFilterEditor, searchFieldsEditor, tonesEditor } from './views-editors';
import { blockKindAt, classifyView, columnsPointer, fieldLabel, groupPointer, localFindings, nodeOfPath, previewBody, tableIndex, viewTree, type Catalogue, type View } from './views-model';

/**
 * Configuration du socle d'éditeur pour une vue de liste (ADR-130 tranche 2b) : schémas `view-body` et
 * `core-component-inputs`, ordre et libellés des propriétés, éditeurs dédiés, aperçu par le vrai rendu de
 * liste (`ListRepresentation`, celui de la page) sur les données réelles du tenant, en lecture seule : ni
 * action, ni commande de masse, ni création, ni export.
 */

export const VIEW_SCHEMA_ID = 'view-body.schema.json';
export const INPUTS_SCHEMA_ID = 'core-component-inputs.schema.json';

/** Largeurs d'écran du back-office : la liste masque d'abord les colonnes de priorité haute. */
export const VIEW_PREVIEW_FORMATS: readonly PreviewFormat[] = [
  { id: 'web', label: 'Web' },
  { id: 'laptop', label: 'Portable', width: 1280, height: 800, scale: 0.42 },
  { id: 'tablet', label: 'Tablette', width: 820, height: 1180, scale: 0.4 },
  { id: 'mobile', label: 'Mobile', width: 390, height: 844, scale: 0.62 },
];

const LABELS: Readonly<Record<string, string>> = {
  title: 'Titre',
  description: 'Description',
  data_source: 'Source (lecture seule)',
  'data_source.page_size': 'Lignes par chargement',
  field: 'Champ',
  label: 'Libellé de la colonne (sinon celui du champ)',
  cell: 'Cellule',
  'cell.kind': 'Format de cellule',
  'cell.subtitle_field': 'Sous-titre',
  'cell.image_field': 'Image',
  'cell.max': 'Maximum de la barre',
  'cell.warn_at': 'Attention à partir de',
  'cell.danger_at': 'Danger à partir de',
  tones: 'Couleur des valeurs',
  align: 'Alignement',
  priority: 'Priorité sur écran étroit (1 = toujours visible)',
  hideable: 'L’utilisateur peut la masquer',
  format: 'Format',
  decimals: 'Décimales',
  date: 'Forme de la date',
  width: 'Largeur (px)',
  density: 'Densité',
  group_by: 'Regroupement',
  group_collapsed: 'Groupes repliés à l’ouverture',
  period: 'Période',
  'period.field': 'Champ date',
  'period.presets': 'Préréglages proposés',
  'period.default_preset': 'Préréglage par défaut',
  presets: 'Préréglages proposés',
  default_preset: 'Préréglage par défaut',
  sortable: 'Tri par en-tête',
  presentation: 'Présentation',
  show_counts: 'Compteur par filtre',
  quick_filter_id: 'Identifiant',
  default: 'Filtre par défaut',
  filter: 'Conditions',
  fields: 'Champs cherchés',
  placeholder: 'Texte d’aide',
  direction: 'Sens',
};

const ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  'cell.kind': { image_title: 'Vignette et titre', avatar: 'Avatar', progress: 'Barre de progression', chip: 'Pastille contournée', button: 'Bouton' },
  align: { start: 'À gauche', center: 'Centré', end: 'À droite' },
  density: { normal: 'Normale', compact: 'Compacte' },
  presentation: { tabs: 'Onglets', chips: 'Jetons' },
  direction: { asc: 'Croissant', desc: 'Décroissant' },
  default_preset: PRESET_LABELS,
  'period.default_preset': PRESET_LABELS,
};

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const defs = (registry: SchemaRegistry, id: string): Record<string, SchemaNode> => {
  const schema = registry[id];
  if (schema === undefined) throw new Error(`Contrat ${id} absent du registre.`);
  return (schema['$defs'] ?? {}) as Record<string, SchemaNode>;
};
const prop = (node: SchemaNode, ...path: string[]): SchemaNode => path.reduce<SchemaNode>((current, key) => ((current['properties'] as Record<string, SchemaNode> | undefined)?.[key] ?? (key === 'items' ? (current['items'] as SchemaNode) : {})) as SchemaNode, node);

/** Panneau de propriétés d'un bloc de la vue, généré des contrats. */
export function viewProperties(registry: SchemaRegistry, catalogue: Catalogue, view: View, pointer: Pointer): PropertyGroup<View> | null {
  const kind = blockKindAt(view, pointer);
  if (kind === null) return null;
  const viewSchema = registry[VIEW_SCHEMA_ID]!;
  const inputsDoc = registry[INPUTS_SCHEMA_ID]!;
  const inputs = defs(registry, INPUTS_SCHEMA_ID);
  const collection = inputs['collection_inputs']!;
  const table = `/representations/${tableIndex(view)}/inputs`;
  const readable = (field: { readable_by: string[] }): boolean => field.readable_by.length > 0;
  const filterable = (field: { filterable: boolean; envelope?: boolean }): boolean => field.filterable || field.envelope === true;
  switch (kind) {
    case 'view':
      return { title: 'Vue', base: '', descriptors: describeProperties(viewSchema, registry, { include: { '': ['title', 'description', 'data_source'], data_source: ['page_size'] } }), labels: LABELS };
    case 'columns':
      return { title: 'Colonnes', base: table, descriptors: describeProperties(inputs['table_inputs']!, registry, { include: { '': ['density'] } }, '', inputsDoc), labels: LABELS, enumLabels: ENUM_LABELS };
    case 'column': {
      const fieldAt = (at: string): string | undefined => {
        const segments = parsePointer(at);
        const column = getAt(view, `/${segments.slice(0, segments.indexOf('columns') + 2).join('/')}`);
        return isRecord(column) && typeof column['field'] === 'string' ? column['field'] : undefined;
      };
      const field = fieldAt(pointer);
      return {
        title: `Colonne — ${field === undefined ? '' : fieldLabel(catalogue, field)}`,
        base: pointer,
        descriptors: describeProperties(inputs['field_ref']!, registry, { include: { '': ['field', 'label', 'cell', 'tones', 'align', 'format', 'priority', 'hideable', 'width'], cell: ['kind', 'subtitle_field', 'image_field', 'max', 'warn_at', 'danger_at'], format: ['date', 'decimals'] } }, '', inputsDoc),
        labels: LABELS,
        enumLabels: ENUM_LABELS,
        editors: {
          field: fieldEditor(catalogue, readable),
          'cell.subtitle_field': fieldEditor(catalogue, readable),
          'cell.image_field': fieldEditor(catalogue, (entry) => readable(entry) && (entry.type === 'file' || entry.type === 'reference')),
          tones: tonesEditor(catalogue, fieldAt),
        },
      };
    }
    case 'filters':
      return {
        title: 'Filtres proposés',
        base: '/collection/inputs',
        descriptors: describeProperties(collection, registry, { include: { '': ['period', 'sortable'], period: ['field', 'presets', 'default_preset'] } }, '', inputsDoc),
        labels: LABELS,
        enumLabels: ENUM_LABELS,
        editors: { 'period.field': fieldEditor(catalogue, (entry) => (entry.type === 'date' || entry.type === 'datetime') && filterable(entry)), 'period.presets': presetsEditor },
      };
    case 'filter': {
      const index = parsePointer(pointer).at(-1)!;
      const descriptor: PropertyDescriptor = { key: index, kind: 'string', required: true, schema: {} };
      const editors: Record<string, CustomEditor<View>> = { [index]: fieldEditor(catalogue, (entry) => entry.filterable && entry.field_id !== 'entity_id') };
      return { title: 'Filtre proposé', base: '/collection/inputs/filters', descriptors: [descriptor], labels: { [index]: 'Champ filtré' }, editors };
    }
    case 'period':
      return { title: 'Période', base: pointer, descriptors: describeProperties(prop(collection, 'period'), registry, {}, '', inputsDoc), labels: LABELS, enumLabels: ENUM_LABELS, editors: { field: fieldEditor(catalogue, (entry) => (entry.type === 'date' || entry.type === 'datetime') && filterable(entry)), presets: presetsEditor } };
    case 'quick_filters':
      return { title: 'Filtres enregistrés', base: '/collection/inputs/quick_filters', descriptors: describeProperties(prop(collection, 'quick_filters'), registry, { include: { '': ['presentation', 'show_counts'] } }, '', inputsDoc), labels: LABELS, enumLabels: ENUM_LABELS };
    case 'quick_filter':
      return { title: 'Filtre enregistré', base: pointer, descriptors: describeProperties(prop(collection, 'quick_filters', 'items', 'items'), registry, { include: { '': ['label', 'quick_filter_id', 'default', 'filter'] } }, '', inputsDoc), labels: LABELS, editors: { filter: quickFilterEditor(catalogue) } };
    case 'search':
      return { title: 'Recherche', base: pointer, descriptors: describeProperties(prop(collection, 'search'), registry, { include: { '': ['fields', 'placeholder'] } }, '', inputsDoc), labels: LABELS, editors: { fields: searchFieldsEditor(catalogue) } };
    case 'sorts':
      return { title: 'Tri par défaut', base: '', descriptors: [], labels: LABELS };
    case 'sort':
      return { title: 'Tri', base: pointer, descriptors: describeProperties(prop(viewSchema, 'data_source', 'default_sort', 'items'), registry), labels: LABELS, enumLabels: ENUM_LABELS, editors: { field: fieldEditor(catalogue, (entry) => entry.sortable && readable(entry)) } };
    case 'group':
      return { title: 'Regroupement', base: table, descriptors: describeProperties(inputs['table_inputs']!, registry, { include: { '': ['group_by', 'group_collapsed'] } }, '', inputsDoc), labels: LABELS, editors: { group_by: groupEditor(catalogue) } };
    default:
      return null;
  }
}

/** Le rendu ne casse jamais l'éditeur : un brouillon que la liste ne sait pas afficher le dit. */
class PreviewBoundary extends Component<{ children: ReactNode; draft: unknown }, { failure: string | null }> {
  override state = { failure: null as string | null };
  static getDerivedStateFromError(error: unknown): { failure: string } {
    return { failure: error instanceof Error ? error.message : String(error) };
  }
  override componentDidUpdate(previous: { draft: unknown }): void {
    if (previous.draft !== this.props.draft && this.state.failure !== null) this.setState({ failure: null });
  }
  override render(): ReactNode {
    return this.state.failure === null ? this.props.children : <p role="alert" className="ay-field__error">L’aperçu ne sait pas afficher ce brouillon : {this.state.failure}</p>;
  }
}

/**
 * Aperçu : la liste réelle (`ListRepresentation`) sur les données du tenant, sous l'autorisation de la
 * personne qui édite. Composants de la vue résolue, aucune commande : rien ne peut s'exécuter d'ici.
 */
export function ViewPreview({ view, resolution }: { view: View; resolution: ViewResolution }) {
  const services = useAppServices();
  const scoped = useMemo(() => {
    const components = new Map(services.components);
    for (const component of resolution.components) components.set(component.artifact_id, component.body);
    return { ...services, components, commands: new Map() };
  }, [services, resolution]);
  const body = useMemo(() => previewBody(view), [view]);
  return (
    <AppServicesContext.Provider value={scoped}>
      <div className="ay-views-preview" aria-label="Aperçu de la liste (lecture seule)">
        <p className="ay-text ay-text--muted ay-views-preview__note">Données réelles du tenant, en lecture seule : ce que vous voyez dépend de vos droits.</p>
        <PreviewBoundary draft={view}>
          <ListRepresentation view={body} object={resolution.object} onOpen={() => undefined} selectedId={null} refreshKey={0} />
        </PreviewBoundary>
      </div>
    </AppServicesContext.Provider>
  );
}

export function viewsEditorConfig(registry: SchemaRegistry, catalogue: Catalogue, resolution: ViewResolution): EditorConfig<View> {
  return {
    noun: 'Vue',
    tree: (view) => viewTree(view, catalogue),
    properties: (view, pointer) => viewProperties(registry, catalogue, view, pointer),
    localFindings: (view) => localFindings(view, catalogue),
    classify: classifyView,
    nodeOf: nodeOfPath,
    renderPreview: (view) => <ViewPreview view={view} resolution={resolution} />,
    formats: VIEW_PREVIEW_FORMATS,
  };
}

export { columnsPointer, groupPointer };
